/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Input, Select, Switch, Table, Tooltip, notification, Pagination } from 'antd';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { Button as CrceButton, Spinner } from '@crc-evans/ui-components';
import { Contact, Pencil, Plus, X } from 'lucide-react';
import {
  createRole,
  fetchActiveRoles,
  fetchAllRoles,
  formatPersonnelModuleLabel,
  toFormPersonnelRoleType,
  PERSONNEL_MODULE_OPTIONS,
  updateRole,
  normalizePersonnelRoleType,
} from '../../services/personnelRoleService';
import './ConsumableMaterialAdmin.css';
import '../../pages/technical/HeatProfilePage.css';

const { Option } = Select;

const ROLE_COLUMN_FILTERS_EMPTY = {
  name: '',
  personnelRoleType: '',
  status: '',
};

const normalizeStatus = (value) =>
  String(value || 'active').toLowerCase() === 'inactive' ? 'inactive' : 'active';

const RolesAdmin = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [columnFilters, setColumnFilters] = useState(() => ({ ...ROLE_COLUMN_FILTERS_EMPTY }));
  const [sortKey, setSortKey] = useState('name');
  const [sortDesc, setSortDesc] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = showInactive
        ? await fetchAllRoles({ pageSize: 100, pageNumber: 1 })
        : await fetchActiveRoles({ pageSize: 100, pageNumber: 1 });
      const rows = Array.isArray(result?.rows) ? result.rows : [];
      setData(rows.map((item, i) => ({ ...item, sno: i + 1 })));
    } catch {
      notification.error({ message: 'Failed to load roles' });
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!modalVisible) return;
    if (editRecord) {
      form.setFieldsValue({
        name: editRecord.name ?? '',
        personnelRoleType: toFormPersonnelRoleType(editRecord),
        status: normalizeStatus(editRecord.status),
      });
      return;
    }
    form.setFieldsValue({
      name: '',
      personnelRoleType: undefined,
      status: 'active',
    });
  }, [modalVisible, editRecord, form]);

  const handleColumnFilterChange = useCallback((key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleSortClick = useCallback((field) => {
    setSortKey((prev) => {
      if (prev === field) {
        setSortDesc((d) => !d);
        return prev;
      }
      setSortDesc(false);
      return field;
    });
  }, []);

  const handleAdd = useCallback(() => {
    setEditRecord(null);
    setModalVisible(true);
  }, []);

  const handleEdit = useCallback((record) => {
    setEditRecord(record);
    setModalVisible(true);
  }, []);

  const handleStatusChange = useCallback(
    async (record, nextStatus) => {
      if (!record?.id) return;
      setStatusUpdatingId(record.id);
      try {
        await updateRole(record.id, { status: nextStatus });
        notification.success({
          message: `Role marked as ${nextStatus === 'active' ? 'Active' : 'Inactive'}`,
        });
        await fetchData();
      } catch {
        notification.error({ message: 'Failed to update role status' });
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [fetchData]
  );

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const personnelRoleType = normalizePersonnelRoleType(values.personnelRoleType);
      if (!personnelRoleType) {
        notification.error({ message: 'Role type is required' });
        return;
      }
      const payload = {
        name: String(values.name || '').trim(),
        personnelRoleType,
        status: normalizeStatus(values.status),
      };
      if (editRecord) {
        await updateRole(editRecord.id, payload);
        notification.success({ message: 'Role updated successfully' });
      } else {
        await createRole(payload);
        notification.success({ message: 'Role added successfully' });
      }
      setModalVisible(false);
      form.resetFields();
      setEditRecord(null);
      await fetchData();
    } catch (err) {
      if (err?.errorFields) return;
      notification.error({ message: 'Failed to save role' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setEditRecord(null);
  };

  const filteredList = useMemo(() => {
    let list = [...(data || [])];
    const lc = (v) => (v == null ? '' : String(v)).trim().toLowerCase();
    if (columnFilters.name.trim()) {
      const q = lc(columnFilters.name);
      list = list.filter((row) => lc(row.name).includes(q));
    }
    if (columnFilters.personnelRoleType.trim()) {
      const q = lc(columnFilters.personnelRoleType);
      list = list.filter((row) => lc(formatPersonnelModuleLabel(row)).includes(q));
    }
    if (columnFilters.status.trim()) {
      const q = lc(columnFilters.status);
      list = list.filter((row) => normalizeStatus(row.status).includes(q));
    }
    return list;
  }, [data, columnFilters]);

  const sortedList = useMemo(() => {
    const list = [...filteredList];
    list.sort((a, b) => {
      let va = '';
      let vb = '';
      if (sortKey === 'personnelRoleType') {
        va = formatPersonnelModuleLabel(a);
        vb = formatPersonnelModuleLabel(b);
      } else {
        va = (a[sortKey] ?? '').toString();
        vb = (b[sortKey] ?? '').toString();
      }
      const cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
      return sortDesc ? -cmp : cmp;
    });
    return list;
  }, [filteredList, sortKey, sortDesc]);

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(sortedList.length / pageSize) || 1);
    setPage((p) => (p > lastPage ? lastPage : p));
  }, [sortedList.length, pageSize]);

  const pagedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedList.slice(start, start + pageSize);
  }, [sortedList, page, pageSize]);

  const renderSortCaret = useCallback(
    (field) =>
      sortKey === field ? (
        sortDesc ? (
          <CaretDownOutlined className="equipment-type-sort-dir-icon" aria-hidden />
        ) : (
          <CaretUpOutlined className="equipment-type-sort-dir-icon" aria-hidden />
        )
      ) : (
        <CaretUpOutlined
          className="equipment-type-sort-dir-icon doc-control-sort-caret-inactive"
          aria-hidden
        />
      ),
    [sortKey, sortDesc]
  );

  const renderColumnTitle = useCallback(
    (label, dataIndex, sortField) => (
      <div className="column-header-with-filter">
        <div className="column-header-text-wrapper spares-column-header-sortable">
          <span>{label}</span>
          <span
            className="custom-sort-icon"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleSortClick(sortField);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSortClick(sortField);
              }
            }}
          >
            {renderSortCaret(sortField)}
          </span>
        </div>
        <div className="header-filter-input user-mgmt-header-search">
          <div className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm heat-profile-header-inline-shell">
            <Input
              placeholder="Search"
              value={columnFilters[dataIndex] ?? ''}
              onChange={(e) => handleColumnFilterChange(dataIndex, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              size="small"
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </div>
    ),
    [columnFilters, handleColumnFilterChange, handleSortClick, renderSortCaret]
  );

  const columns = useMemo(
    () => [
      {
        title: renderColumnTitle('S.No', 'sno', 'sno'),
        dataIndex: 'sno',
        key: 'sno',
        width: 88,
        align: 'center',
        render: (_, __, index) => (page - 1) * pageSize + index + 1,
      },
      {
        title: renderColumnTitle('Name', 'name', 'name'),
        dataIndex: 'name',
        key: 'name',
        width: 320,
        minWidth: 280,
        ellipsis: true,
      },
      {
        title: renderColumnTitle('Role Type', 'personnelRoleType', 'personnelRoleType'),
        dataIndex: 'personnelRoleType',
        key: 'personnelRoleType',
        width: 180,
        minWidth: 160,
        render: (_, record) => formatPersonnelModuleLabel(record),
      },
      {
        title: renderColumnTitle('Status', 'status', 'status'),
        dataIndex: 'status',
        key: 'status',
        width: 160,
        minWidth: 140,
        align: 'center',
        render: (val) => {
          const isActive = normalizeStatus(val) === 'active';
          return (
            <span
              className={`cm-status-badge ${
                isActive ? 'cm-status-badge--active' : 'cm-status-badge--inactive'
              }`}
            >
              <span
                className={`cm-status-dot ${
                  isActive ? 'cm-status-dot--active' : 'cm-status-dot--inactive'
                }`}
              />
              {isActive ? 'Active' : 'Inactive'}
            </span>
          );
        },
      },
      {
        title: <span className="equipment-type-column-title">Actions</span>,
        key: 'actions',
        width: 140,
        minWidth: 130,
        align: 'center',
        fixed: 'right',
        render: (_, record) => {
          const isActive = normalizeStatus(record.status) === 'active';
          const isUpdating = statusUpdatingId === record.id;
          return (
            <div className="cm-actions-cell roles-admin-actions-cell">
              <Tooltip title="Edit">
                <button
                  type="button"
                  className="cm-action-btn cm-action-edit roles-admin-action-btn"
                  onClick={() => handleEdit(record)}
                >
                  <Pencil size={18} strokeWidth={2} />
                </button>
              </Tooltip>
              <Tooltip title={isActive ? 'Make Inactive' : 'Make Active'}>
                <span className="roles-admin-row-switch-wrap">
                  <Switch
                    className="roles-admin-row-switch"
                    checked={isActive}
                    disabled={isUpdating}
                    size="small"
                    onChange={(checked) =>
                      handleStatusChange(record, checked ? 'active' : 'inactive')
                    }
                  />
                </span>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [page, pageSize, handleEdit, handleStatusChange, statusUpdatingId, renderColumnTitle]
  );

  return (
    <div className="modern-cm-page roles-admin-page">
      <div className="heat-profile-content-wrapper">
        <div className="heat-profile-card">
          <div className="heat-profile-list-header">
            <div className="heat-profile-header-left">
              <div
                className="heat-profile-icon-wrap"
                style={{ backgroundColor: '#faf5ff', color: '#7e22ce' }}
              >
                <Contact size={22} color="currentColor" strokeWidth={2} />
              </div>
              <div className="heat-profile-header-content">
                <div className="heat-profile-title-row">
                  <h3 className="heat-profile-list-title">Roles</h3>
                </div>
                <p className="heat-profile-list-description">
                  Manage global roles for personnel and technician logs
                </p>
              </div>
            </div>
            <div className="heat-profile-header-actions calibration-modern-actions">
              <label className="roles-admin-show-inactive">
                <Switch
                  className="roles-admin-header-switch"
                  checked={showInactive}
                  onChange={setShowInactive}
                  size="small"
                />
                <span>Show inactive</span>
              </label>
              <CrceButton
                label="Add Role"
                variant="primary"
                size="small"
                icon={Plus}
                onClick={handleAdd}
              />
            </div>
          </div>

          <div className="spares-list-table-pager-wrap pipeline-table cm-table-pager-wrap">
            <div className="heat-profile-table-wrap">
              <Table
                className="heat-profile-table spares-list-table h-100 equipPaginationTable equipment-types-table doc-control-users-table"
                rowKey={(r) => String(r.id)}
                columns={columns}
                dataSource={pagedData}
                loading={{
                  spinning: loading,
                  indicator: <Spinner variant="inline" size="large" color="#00C9A7" />,
                }}
                size="middle"
                scroll={{ x: 1200 }}
                pagination={false}
              />
            </div>
            {sortedList.length > 0 && (
              <div className="spares-list-pager-outer">
                <Pagination
                  className="heat-profile-pagination"
                  current={page}
                  pageSize={pageSize}
                  total={sortedList.length}
                  showSizeChanger
                  pageSizeOptions={['10', '20', '50', '100']}
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                  onChange={(p, size) => {
                    setPage(p);
                    if (size != null) setPageSize(size);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {modalVisible && (
        <div className="cm-modal-backdrop" onClick={handleCancel} role="presentation">
          <div className="cm-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="cm-modal-head">
              <h3 className="cm-modal-title">{editRecord ? 'Edit Role' : 'Add Role'}</h3>
              <button
                type="button"
                className="cm-modal-close"
                onClick={handleCancel}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="cm-modal-body">
              <Form form={form} layout="vertical" autoComplete="off" name="roles-admin-form">
                <div className="cm-modal-form-grid roles-admin-modal-form">
                  <div className="cm-modal-form-row roles-admin-modal-form-row">
                    <div className="cm-modal-field">
                      <label>
                        Name <span className="required">*</span>
                      </label>
                      <Form.Item
                        name="name"
                        rules={[{ required: true, message: 'Name is required' }]}
                        noStyle
                      >
                        <Input className="cm-modal-input" placeholder="Enter role name" />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="cm-modal-form-row roles-admin-modal-form-row">
                    <div className="cm-modal-field">
                      <label>
                        Role Type <span className="required">*</span>
                      </label>
                      <Form.Item
                        name="personnelRoleType"
                        rules={[{ required: true, message: 'Role type is required' }]}
                        noStyle
                      >
                        <Select
                          className="cm-modal-select"
                          placeholder="Select role type"
                          dropdownClassName="heat-profile-select-dropdown cm-modal-select-dropdown"
                          dropdownMatchSelectWidth
                          getPopupContainer={() => document.body}
                        >
                          {PERSONNEL_MODULE_OPTIONS.map((opt) => (
                            <Option key={opt.value} value={opt.value}>
                              {opt.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="cm-modal-form-row roles-admin-modal-form-row roles-admin-status-field">
                    <div className="cm-modal-field">
                      <label>Status</label>
                      <Form.Item name="status" noStyle>
                        <Select
                          className="cm-modal-select"
                          placeholder="Select status"
                          dropdownClassName="heat-profile-select-dropdown cm-modal-select-dropdown"
                          dropdownMatchSelectWidth
                          getPopupContainer={() => document.body}
                        >
                          <Option value="active">Active</Option>
                          <Option value="inactive">Inactive</Option>
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </Form>
            </div>
            <div className="cm-modal-foot">
              <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={handleCancel} />
              <CrceButton
                label={saving ? 'Saving...' : editRecord ? 'Update' : 'Add'}
                variant="primary"
                size="medium"
                onClick={handleSave}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesAdmin;
